import { useEffect, useRef } from "preact/hooks";
import { effect } from "@preact/signals";
import {
  AttributionControl,
  GeolocateControl,
  Map as MLMap,
  NavigationControl,
  ScaleControl,
  addProtocol,
  type MapGeoJSONFeature,
  type PointLike,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";

import { buildStyle } from "../map/style";
import { addIcons } from "../map/icons";
import { ensureOverlays, pointLayerIds, setLayerData, setSelectedFeature, srcId } from "../map/overlays";
import { ensureTowns, raiseBasemapTowns } from "../map/towns";
import { MATCH_LAYER, MATCH_SRC, ensureEventRings, setEventVenues } from "../map/events";
import { LIVE_SRC, ensureLiveTrainsOverlay, startLiveTrains, stopLiveTrains } from "../map/liveTrains";
import { RAIL_SRC, ensureRailLines, setClosedLinesVisible, setRailLinesVisible } from "../map/railLines";
import { LAYER_ORDER } from "../data/layers";
import { IRELAND_BOUNDS, IRELAND_CENTER } from "../utils/geo";
import { dataError, eventVenues, featuresByKey, filteredByLayer, liveTrainsOn, mapBounds, mapFocus, myLocation, select, selectedVenue, selected, showClosedLines, showRailLines, theme } from "../state/store";
import { FEEDS } from "../data/events";

const TILES_ASSET_URL = import.meta.env.VITE_TILES_URL ?? import.meta.env.BASE_URL + "tiles/ireland.pmtiles";
const COARSE = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
// Phone-width viewports get slightly smaller map labels.
const COMPACT = typeof window !== "undefined" && window.innerWidth < 600;

const protocol = new Protocol();
addProtocol("pmtiles", protocol.tile);

export function MapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MLMap | null>(null);
  const errorRef = useRef<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = new MLMap({
      container: containerRef.current,
      style: buildStyle(theme.peek(), TILES_ASSET_URL),
      center: IRELAND_CENTER,
      zoom: 6.2,
      minZoom: 5.5,
      maxZoom: 17.5,
      maxBounds: [[IRELAND_BOUNDS[0] - 1.5, IRELAND_BOUNDS[1] - 0.8], [IRELAND_BOUNDS[2] + 1.5, IRELAND_BOUNDS[3] + 0.8]],
      attributionControl: false,
    });
    mapRef.current = map;
    // Test/debug handle (headless screenshots, the offline test); harmless in production.
    (window as unknown as { __map: MLMap }).__map = map;

    map.addControl(new NavigationControl({ showCompass: false, visualizePitch: false }), "top-right");
    const geolocate = new GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
    });
    map.addControl(geolocate, "top-right");
    geolocate.on("geolocate", (e: { coords: { latitude: number; longitude: number } }) => {
      myLocation.value = { lat: e.coords.latitude, lon: e.coords.longitude };
    });
    map.addControl(new ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-left");
    map.addControl(new AttributionControl({ compact: true }), "bottom-right");

    const pushAll = () => {
      const data = filteredByLayer.peek();
      for (const id of LAYER_ORDER) setLayerData(map, id, data[id].map((x) => x.f));
      setSelectedFeature(map, selected.peek());
    };

    // Everything we add on top of the basemap, in stacking order (see overlays.ts).
    // Runs on first load and again after every style rebuild (theme change).
    const applyOverlays = (t: "light" | "dark") => {
      addIcons(map, t);
      ensureRailLines(map, t);
      ensureEventRings(map); // under the pins: a ring around a venue with something on in the window
      ensureOverlays(map, t, COMPACT);
      ensureTowns(map, t, COMPACT);
      ensureLiveTrainsOverlay(map);
      raiseBasemapTowns(map);
      pushAll();
      setEventVenues(map, eventVenues.peek());
      setRailLinesVisible(map, showRailLines.peek());
      setClosedLinesVisible(map, showClosedLines.peek());
      if (liveTrainsOn.peek()) startLiveTrains(map);
    };
    const onStyleReady = () => applyOverlays(theme.peek());
    map.on("load", onStyleReady);
    map.on("style.load", () => { if (map.loaded()) onStyleReady(); });

    map.on("error", (e) => {
      const msg = e.error instanceof Error ? e.error.message : String(e.error ?? "");
      if (/pmtiles|Range|ireland\.pmtiles/i.test(msg) && !errorRef.current) {
        errorRef.current = msg;
        dataError.value = `Basemap tiles failed to load (${msg}). Run scripts/build-tiles.sh so web/public/tiles/ireland.pmtiles exists, or set VITE_TILES_URL.`;
        return;
      }
      // MapLibre reports a rejected layer/style spec here instead of throwing, so
      // without this a bad paint expression just silently draws nothing.
      console.warn("map error:", msg);
    });

    // Viewport → list filter.
    const publishBounds = () => {
      const b = map.getBounds();
      mapBounds.value = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    };
    let throttled = false;
    map.on("move", () => {
      if (throttled) return;
      throttled = true;
      setTimeout(() => { throttled = false; publishBounds(); }, 120);
    });
    map.on("moveend", publishBounds);
    map.once("load", publishBounds);

    // Click: nearest pin (icon or dot) inside a padded box wins.
    const pad = COARSE ? 14 : 6;
    map.on("click", (e) => {
      const box: [PointLike, PointLike] = [[e.point.x - pad, e.point.y - pad], [e.point.x + pad, e.point.y + pad]];
      const pl = pointLayerIds(map);
      const hits = pl.length ? map.queryRenderedFeatures(box, { layers: pl }) : [];
      let best: MapGeoJSONFeature | null = null, bestD = Infinity;
      for (const h of hits) {
        const c = (h.geometry as GeoJSON.Point).coordinates;
        const p = map.project([c[0], c[1]]);
        const d = (p.x - e.point.x) ** 2 + (p.y - e.point.y) ** 2;
        if (d < bestD) { bestD = d; best = h; }
      }
      if (best) {
        const f = featuresByKey.peek().get(String(best.properties?.key));
        if (f) select(f);
        return;
      }
      // An event ring with no pin under it (a club ground or a point-to-point field that
      // isn't in a layer): open that venue's own sheet, with what is on there.
      const ring = map.getLayer(MATCH_LAYER) ? map.queryRenderedFeatures(box, { layers: [MATCH_LAYER] })[0] : undefined;
      if (ring) {
        const feed = String(ring.properties?.feed ?? "gaa") as keyof typeof FEEDS;
        const layer = FEEDS[feed]?.layer ?? "gaa";
        const g = ring.properties?.place ? featuresByKey.peek().get(`${layer}:${ring.properties.place}`) : null;
        if (g) { select(g); return; }
        // Rendered geometry is tile-quantised, so match on name and take the nearest of
        // any namesakes.
        const name = String(ring.properties?.name ?? "");
        const c = (ring.geometry as GeoJSON.Point).coordinates;
        let v = null as (typeof eventVenues.value)[number] | null, vd = Infinity;
        for (const m of eventVenues.peek()) {
          if (m.name !== name) continue;
          const d = (m.lon - c[0]) ** 2 + (m.lat - c[1]) ** 2;
          if (d < vd) { vd = d; v = m; }
        }
        if (v) { select(null); selectedVenue.value = v; return; }
      }
      select(null);
      selectedVenue.value = null;
    });
    map.on("mousemove", (e) => {
      const layers = [...pointLayerIds(map), ...(map.getLayer(MATCH_LAYER) ? [MATCH_LAYER] : [])];
      if (!layers.length) return;
      map.getCanvas().style.cursor = map.queryRenderedFeatures(e.point, { layers }).length ? "pointer" : "";
    });

    // Reactive: filtered data → sources.
    const disposers = [
      effect(() => {
        const data = filteredByLayer.value;
        if (!map.getSource(srcId("history"))) return;
        for (const id of LAYER_ORDER) setLayerData(map, id, data[id].map((x) => x.f));
      }),
      effect(() => {
        const f = selected.value;
        if (map.getSource("selected")) setSelectedFeature(map, f);
      }),
      effect(() => {
        const req = mapFocus.value;
        if (!req) return;
        if (req.bbox) {
          map.fitBounds([[req.bbox[0], req.bbox[1]], [req.bbox[2], req.bbox[3]]], { padding: 48, duration: 700, maxZoom: 14 });
        } else {
          map.flyTo({ center: [req.lon, req.lat], zoom: req.zoom ?? Math.max(map.getZoom(), 13.5), duration: 900, essential: true });
        }
      }),
      effect(() => {
        const t = theme.value;
        if (!map.isStyleLoaded() && !map.loaded()) return;
        map.setStyle(buildStyle(t, TILES_ASSET_URL), { diff: false });
        map.once("style.load", () => applyOverlays(t));
      }),
      effect(() => {
        const v = eventVenues.value;
        if (!map.getSource(MATCH_SRC)) return; // not ready yet — onStyleReady applies the initial state
        setEventVenues(map, v);
      }),
      effect(() => {
        const on = showRailLines.value;
        if (!map.getSource(RAIL_SRC)) return; // not ready yet — onStyleReady applies the initial state
        setRailLinesVisible(map, on);
      }),
      effect(() => {
        const on = showClosedLines.value;
        if (!map.getSource(RAIL_SRC)) return; // not ready yet — onStyleReady applies the initial state
        setClosedLinesVisible(map, on);
      }),
      effect(() => {
        const on = liveTrainsOn.value;
        if (!map.getSource(LIVE_SRC)) return; // not ready yet — onStyleReady applies the initial state
        if (on) startLiveTrains(map); else stopLiveTrains();
      }),
    ];

    return () => {
      disposers.forEach((d) => d());
      stopLiveTrains();
      map.remove();
      mapRef.current = null;
      mapBounds.value = null;
    };
  }, []);

  return <div ref={containerRef} class="map-container" role="application" aria-label="Map of Ireland" />;
}
